const SOC_COOKIEBOT_CONSENT_CHANGED_EVENT = "soc:cookiebot-consent-change";
const SOC_COOKIEBOT_CMP_LOADED_EVENT = "soc:cookiebot-cmp-loaded";

const CATEGORY = {
  ESSENTIAL: "essential",
  PERFORMANCE: "performance",
  ANALYTICS: "analytics",
  ADVERTISING: "advertising",
  SOCIAL_NETWORKING: "social_networking",
  UNCLASSIFIED: "unclassified",
};

const TOOL = {
  MIXPANEL: "mixpanel",
  GTM: "gtm",
  EXPERIMENTS: "experiments",
};

const TOOL_CATEGORY = {
  [TOOL.MIXPANEL]: [CATEGORY.ANALYTICS],
  [TOOL.GTM]: [CATEGORY.ANALYTICS],
  [TOOL.EXPERIMENTS]: [CATEGORY.PERFORMANCE],
};

const MIXPANEL_STORAGE_KEY_PATTERN = /^mp_.*_mixpanel$/i;
const MIXPANEL_TAB_STORAGE_KEY_PATTERN = /^mp_(?:gen_new_)?tab_id_mixpanel_/i;
const MIXPANEL_QUEUE_STORAGE_KEY_PATTERN = /^__mpq_.*_ev$/i;

const hasBrowserApis = () =>
  typeof window !== "undefined" && typeof window.document !== "undefined";

const getEnvValue = (key) => {
  if (
    typeof process !== "undefined" &&
    process.env &&
    process.env[key] != null
  ) {
    return process.env[key];
  }
  if (hasBrowserApis() && window.__ENV && window.__ENV[key] != null) {
    return window.__ENV[key];
  }
  return undefined;
};

const isEnabledValue = (value) =>
  ["1", "true", "yes", "on"].includes(
    String(value || "")
      .trim()
      .toLowerCase(),
  );

const isCookiebotCmpEnabled = () =>
  isEnabledValue(getEnvValue("ENABLE_COOKIEBOT_CMP")) ||
  isEnabledValue(getEnvValue("GATSBY_ENABLE_COOKIEBOT_CMP"));

const isConsentCmpEnabled = () => isCookiebotCmpEnabled();

const normalizeCategory = (category) =>
  String(category || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_")
    .replace(/^performance_and_functionality$/, CATEGORY.PERFORMANCE)
    .replace(/^analytics_and_customization$/, CATEGORY.ANALYTICS)
    .replace(/^strictly_necessary$/, CATEGORY.ESSENTIAL);

const normalizeConsentState = (state) => {
  if (!state) return new Set();

  if (Array.isArray(state)) {
    return new Set(state.map(normalizeCategory).filter(Boolean));
  }

  if (Array.isArray(state.categories)) {
    return new Set(state.categories.map(normalizeCategory).filter(Boolean));
  }

  const categories = Object.entries(state)
    .filter(
      ([, value]) => value === true || value === "true" || value === "granted",
    )
    .map(([key]) => normalizeCategory(key))
    .filter(Boolean);

  return new Set(categories);
};

const readCookiebotConsentState = () => {
  if (!hasBrowserApis() || !window.Cookiebot) return new Set();

  const consent = window.Cookiebot.consent || {};
  const categories = [CATEGORY.ESSENTIAL];

  if (consent.statistics === true) categories.push(CATEGORY.ANALYTICS);
  if (consent.preferences === true) categories.push(CATEGORY.PERFORMANCE);
  if (consent.marketing === true) categories.push(CATEGORY.ADVERTISING);

  return new Set(categories);
};

const isCategoryAllowed = (category, state) => {
  if (!isConsentCmpEnabled()) return true;
  return (state || readCookiebotConsentState()).has(normalizeCategory(category));
};

const isToolAllowed = (tool, state) => {
  if (!isConsentCmpEnabled()) return true;
  const consentState = state || readCookiebotConsentState();
  const requiredCategories = TOOL_CATEGORY[tool] || [];
  return requiredCategories.every((category) => consentState.has(category));
};

// True once Cookiebot has a user response (accept/decline), or when the CMP is off.
// Until then, experiment assignment is unknown and must not be treated as SiteA.
const isConsentDecisionReady = () => {
  if (!isConsentCmpEnabled()) return true;
  if (!hasBrowserApis() || !window.Cookiebot) return false;
  if (typeof window.Cookiebot.hasResponse === "boolean") {
    return window.Cookiebot.hasResponse === true;
  }
  // Older Cookiebot builds: presence after ConsentReady is enough.
  return Boolean(window.Cookiebot.consent);
};

const removeMatchingStorage = (storage, predicate) => {
  if (!storage) return;

  for (let index = storage.length - 1; index >= 0; index -= 1) {
    const key = storage.key(index);
    if (key && predicate(key)) {
      storage.removeItem(key);
    }
  }
};

const clearControlledStorage = ({
  analyticsAllowed = false,
  performanceAllowed = false,
} = {}) => {
  if (!hasBrowserApis()) return;

  if (!analyticsAllowed) {
    removeMatchingStorage(
      window.localStorage,
      (key) =>
        MIXPANEL_STORAGE_KEY_PATTERN.test(key) ||
        MIXPANEL_TAB_STORAGE_KEY_PATTERN.test(key) ||
        MIXPANEL_QUEUE_STORAGE_KEY_PATTERN.test(key),
    );
    removeMatchingStorage(
      window.sessionStorage,
      (key) =>
        MIXPANEL_TAB_STORAGE_KEY_PATTERN.test(key) ||
        MIXPANEL_QUEUE_STORAGE_KEY_PATTERN.test(key),
    );
  }

  if (!performanceAllowed) {
    window.localStorage?.removeItem("_soc_experiments");
    window.localStorage?.removeItem("_soc_experiment_assignments_v1");
    window.localStorage?.removeItem("_soc_anonymous_participant_id");
  }
};

// Without an explicit state, wait for a Cookiebot decision: before uc.js loads
// the consent set is empty, which would wipe storage a visitor consented to.
const clearControlledStorageForConsent = (state) => {
  if (!state && !isConsentDecisionReady()) return;
  const consentState = state || readCookiebotConsentState();

  clearControlledStorage({
    analyticsAllowed: isCategoryAllowed(CATEGORY.ANALYTICS, consentState),
    performanceAllowed: isCategoryAllowed(CATEGORY.PERFORMANCE, consentState),
  });
};

const dispatchConsentChange = (state) => {
  if (!hasBrowserApis()) return;
  const categories = Array.from(state || readCookiebotConsentState());
  const event =
    typeof CustomEvent === "function"
      ? new CustomEvent(SOC_COOKIEBOT_CONSENT_CHANGED_EVENT, {
          detail: { categories },
        })
      : {
          type: SOC_COOKIEBOT_CONSENT_CHANGED_EVENT,
          detail: { categories },
        };
  window.dispatchEvent(event);
};

const handleCookiebotConsentUpdate = (data) => {
  const state = data ? normalizeConsentState(data) : readCookiebotConsentState();
  if (data || isConsentDecisionReady()) clearControlledStorageForConsent(state);
  dispatchConsentChange(state);
};

const initializeCookiebotConsentBridge = () => {
  if (!hasBrowserApis() || !isConsentCmpEnabled()) return;
  if (window.__socCookiebotConsentBridgeInitialized) return;
  window.__socCookiebotConsentBridgeInitialized = true;

  handleCookiebotConsentUpdate();
};

const onConsentChange = (callback) => {
  if (!hasBrowserApis()) return () => {};

  const listener = () => {
    initializeCookiebotConsentBridge();
    const state = readCookiebotConsentState();
    if (isConsentDecisionReady()) clearControlledStorageForConsent(state);
    callback(state);
  };
  window.addEventListener(SOC_COOKIEBOT_CONSENT_CHANGED_EVENT, listener);
  window.addEventListener(SOC_COOKIEBOT_CMP_LOADED_EVENT, listener);
  window.addEventListener("CookiebotOnAccept", listener);
  window.addEventListener("CookiebotOnDecline", listener);
  window.addEventListener("CookiebotOnConsentReady", listener);

  return () => {
    window.removeEventListener(SOC_COOKIEBOT_CONSENT_CHANGED_EVENT, listener);
    window.removeEventListener(SOC_COOKIEBOT_CMP_LOADED_EVENT, listener);
    window.removeEventListener("CookiebotOnAccept", listener);
    window.removeEventListener("CookiebotOnDecline", listener);
    window.removeEventListener("CookiebotOnConsentReady", listener);
  };
};

module.exports = {
  CATEGORY,
  TOOL,
  SOC_COOKIEBOT_CMP_LOADED_EVENT,
  SOC_COOKIEBOT_CONSENT_CHANGED_EVENT,
  clearControlledStorage,
  clearControlledStorageForConsent,
  dispatchConsentChange,
  initializeCookiebotConsentBridge,
  isCategoryAllowed,
  isCookiebotCmpEnabled,
  isConsentCmpEnabled,
  isConsentDecisionReady,
  isToolAllowed,
  normalizeConsentState,
  onConsentChange,
  readCookiebotConsentState,
};
